/**
 * Fantasy Console 0 - Host Controller
 *
 * This class runs in the parent page and manages the sandboxed console iframe.
 * It provides a clean API for loading carts and controlling the console.
 */

import {
  ParentToConsoleMessage,
  ConsoleToParentMessage,
  ErrorPayload,
  LogPayload,
  Cart,
} from '@fc0/types'

export type ReadyCallback = () => void
export type ErrorCallback = (error: ErrorPayload) => void
export type LogCallback = (log: LogPayload) => void
export type CartLoadedCallback = (title: string) => void
export type FrozenCallback = () => void
export type BootCompleteCallback = () => void

export interface ConsoleHostOptions {
  iframeSrc?: string
}

export class ConsoleHost {
  private iframe: HTMLIFrameElement
  private container: HTMLElement
  private ready = false
  private pendingCart: Cart | null = null
  private pendingStartBoot = false
  private iframeSrc: string

  // Event callbacks
  private onReadyCallbacks: ReadyCallback[] = []
  private onErrorCallbacks: ErrorCallback[] = []
  private onLogCallbacks: LogCallback[] = []
  private onCartLoadedCallbacks: CartLoadedCallback[] = []
  private onFrozenCallbacks: FrozenCallback[] = []
  private onBootCompleteCallbacks: BootCompleteCallback[] = []

  // Pending capture promise resolver
  private pendingCaptureResolve: ((dataUrl: string | null) => void) | null =
    null

  // Watchdog state
  private watchdogTimer: ReturnType<typeof setInterval> | null = null
  private lastHeartbeat = 0
  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      this.lastHeartbeat = Date.now()
    }
  }

  constructor(container: HTMLElement, options: ConsoleHostOptions = {}) {
    this.container = container

    // Create the iframe
    this.iframe = document.createElement('iframe')
    this.iframe.style.border = 'none'
    this.iframe.style.width = '100%'
    this.iframe.style.height = '100%'

    // Apply sandbox restrictions
    // allow-scripts: enables JavaScript execution
    // NO allow-same-origin: prevents access to parent
    // NO allow-forms: prevents form submission
    // NO allow-popups: prevents opening windows
    // NO allow-top-navigation: prevents navigation
    this.iframe.sandbox.add('allow-scripts')

    // Allow audio playback inside the sandboxed iframe
    this.iframe.allow = 'autoplay'

    // Set the iframe source
    this.iframeSrc = options.iframeSrc || '/iframe.html'
    this.iframe.src = this.iframeSrc

    // Listen for messages from the iframe
    window.addEventListener('message', this.handleMessage)

    // Add iframe to container
    this.container.appendChild(this.iframe)
  }

  /**
   * Send a message to the console iframe
   */
  private sendMessage(message: ParentToConsoleMessage): void {
    if (!this.iframe.contentWindow) {
      console.error('Iframe not ready')
      return
    }
    this.iframe.contentWindow.postMessage(message, '*')
  }

  /**
   * Handle messages from the console iframe
   */
  private handleMessage = (event: MessageEvent): void => {
    // Only process messages from our iframe
    if (event.source !== this.iframe.contentWindow) return

    const message = event.data as ConsoleToParentMessage
    if (!message || typeof message.type !== 'string') return

    switch (message.type) {
      case 'READY':
        this.ready = true
        this.onReadyCallbacks.forEach((cb) => cb())
        // Start pending boot animation if any
        if (this.pendingStartBoot) {
          this.pendingStartBoot = false
          this.sendMessage({ type: 'START_BOOT' })
        }
        // Load pending cart if any
        if (this.pendingCart) {
          this.sendMessage({
            type: 'LOAD_CART',
            payload: { cart: this.pendingCart },
          })
          this.pendingCart = null
        }
        break

      case 'ERROR':
        this.stopWatchdog()
        this.onErrorCallbacks.forEach((cb) => cb(message.payload))
        break

      case 'LOG':
        this.onLogCallbacks.forEach((cb) => cb(message.payload))
        break

      case 'CART_LOADED':
        this.onCartLoadedCallbacks.forEach((cb) => cb(message.payload.title))
        break

      case 'CANVAS_CAPTURED':
        if (this.pendingCaptureResolve) {
          const dataUrl = message.payload.dataUrl || null
          this.pendingCaptureResolve(dataUrl)
          this.pendingCaptureResolve = null
        }
        break

      case 'BOOT_COMPLETE':
        this.onBootCompleteCallbacks.forEach((cb) => cb())
        break

      case 'HEARTBEAT':
        this.lastHeartbeat = Date.now()
        break
    }
  }

  // ============================================================
  // CART OPERATIONS
  // ============================================================

  /**
   * Start the boot animation without loading a cart.
   * Use onBootComplete() to know when the animation finishes.
   */
  startBoot(): void {
    this.startWatchdog()
    if (!this.ready) {
      this.pendingStartBoot = true
      return
    }
    this.sendMessage({ type: 'START_BOOT' })
  }

  /**
   * Load a cart into the console
   */
  loadCart(cart: Cart): Promise<void> {
    this.startWatchdog()
    return new Promise((resolve, reject) => {
      if (!this.ready) {
        // Queue the cart to load when ready
        this.pendingCart = cart
        // Set up one-time callback to resolve when loaded
        const onLoaded = () => {
          this.offCartLoaded(onLoaded)
          resolve()
        }
        const onError = (error: ErrorPayload) => {
          this.offError(onError)
          reject(new Error(error.message))
        }
        this.onCartLoaded(onLoaded)
        this.onError(onError)
        return
      }

      // Clear any pending cart since this new load supersedes it
      this.pendingCart = null

      // Set up one-time callbacks
      const onLoaded = () => {
        this.offCartLoaded(onLoaded)
        this.offError(onError)
        resolve()
      }
      const onError = (error: ErrorPayload) => {
        this.offCartLoaded(onLoaded)
        this.offError(onError)
        reject(new Error(error.message))
      }
      this.onCartLoaded(onLoaded)
      this.onError(onError)

      this.sendMessage({
        type: 'LOAD_CART',
        payload: { cart },
      })
    })
  }

  /**
   * Pause the game loop
   */
  pause(): void {
    this.stopWatchdog()
    this.sendMessage({ type: 'PAUSE' })
  }

  /**
   * Resume the game loop
   */
  resume(): void {
    this.startWatchdog()
    this.sendMessage({ type: 'RESUME' })
  }

  /**
   * Reset the current cart
   */
  reset(): void {
    this.startWatchdog()
    this.sendMessage({ type: 'RESET' })
  }

  /**
   * Execute one frame (when paused)
   */
  step(): void {
    this.sendMessage({ type: 'STEP' })
  }

  /**
   * Set muted state
   */
  setMuted(muted: boolean): void {
    this.sendMessage({ type: 'SET_MUTED', payload: { muted } })
  }

  /**
   * Hot-reload sprite data without restarting the game loop
   */
  updateSprites(sprites: string): void {
    this.sendMessage({ type: 'UPDATE_SPRITES', payload: { sprites } })
  }

  /**
   * Hot-reload map data without restarting the game loop
   */
  updateMap(map: string): void {
    this.sendMessage({ type: 'UPDATE_MAP', payload: { map } })
  }

  /**
   * Hot-reload SFX data without restarting the game loop
   */
  updateSFX(sfx: string): void {
    this.sendMessage({ type: 'UPDATE_SFX', payload: { sfx } })
  }

  /**
   * Capture the current canvas as a data URL
   * @returns Promise that resolves to the data URL or null on timeout/error
   */
  captureCanvas(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.ready) {
        resolve(null)
        return
      }

      // Set up timeout (500ms should be plenty for canvas capture)
      const timeout = setTimeout(() => {
        this.pendingCaptureResolve = null
        resolve(null)
      }, 500)

      // Store resolver
      this.pendingCaptureResolve = (dataUrl: string | null) => {
        clearTimeout(timeout)
        resolve(dataUrl)
      }

      // Send capture request
      this.sendMessage({ type: 'CAPTURE_CANVAS' })
    })
  }

  // ============================================================
  // EVENT LISTENERS
  // ============================================================

  /**
   * Register a callback for when the console is ready
   */
  onReady(callback: ReadyCallback): void {
    this.onReadyCallbacks.push(callback)
    // If already ready, call immediately
    if (this.ready) {
      callback()
    }
  }

  /**
   * Register a callback for runtime errors
   */
  onError(callback: ErrorCallback): void {
    this.onErrorCallbacks.push(callback)
  }

  /**
   * Unregister an error callback
   */
  offError(callback: ErrorCallback): void {
    const index = this.onErrorCallbacks.indexOf(callback)
    if (index !== -1) {
      this.onErrorCallbacks.splice(index, 1)
    }
  }

  /**
   * Register a callback for console log messages
   */
  onLog(callback: LogCallback): void {
    this.onLogCallbacks.push(callback)
  }

  /**
   * Register a callback for when the boot animation completes
   */
  onBootComplete(callback: BootCompleteCallback): void {
    this.onBootCompleteCallbacks.push(callback)
  }

  /**
   * Unregister a boot complete callback
   */
  offBootComplete(callback: BootCompleteCallback): void {
    const index = this.onBootCompleteCallbacks.indexOf(callback)
    if (index !== -1) {
      this.onBootCompleteCallbacks.splice(index, 1)
    }
  }

  /**
   * Register a callback for when a cart is loaded
   */
  onCartLoaded(callback: CartLoadedCallback): void {
    this.onCartLoadedCallbacks.push(callback)
  }

  /**
   * Unregister a cart loaded callback
   */
  offCartLoaded(callback: CartLoadedCallback): void {
    const index = this.onCartLoadedCallbacks.indexOf(callback)
    if (index !== -1) {
      this.onCartLoadedCallbacks.splice(index, 1)
    }
  }

  // ============================================================
  // WATCHDOG
  // ============================================================

  private startWatchdog(): void {
    this.stopWatchdog()
    this.lastHeartbeat = Date.now()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    this.watchdogTimer = setInterval(() => {
      // Skip check when page is hidden (rAF is throttled, no heartbeats expected)
      if (document.hidden) {
        this.lastHeartbeat = Date.now()
        return
      }
      // Skip check when iframe is not visible (e.g. hidden editor tab)
      if (!this.isIframeVisible()) {
        this.lastHeartbeat = Date.now()
        return
      }
      if (Date.now() - this.lastHeartbeat > 3000) {
        this.handleFrozenIframe()
      }
    }, 1000)
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer !== null) {
      clearInterval(this.watchdogTimer)
      this.watchdogTimer = null
    }
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    )
  }

  private isIframeVisible(): boolean {
    if (typeof this.iframe.checkVisibility === 'function') {
      return this.iframe.checkVisibility({ checkVisibilityCSS: true })
    }
    // Fallback: visibility is inherited so this catches ancestor visibility:hidden
    return getComputedStyle(this.iframe).visibility !== 'hidden'
  }

  private handleFrozenIframe(): void {
    this.stopWatchdog()

    // Notify frozen callbacks (lets wrappers show brief error status)
    this.onFrozenCallbacks.forEach((cb) => cb())

    // Recreate the iframe — the auto-loaded cart will produce a
    // meaningful loop-guard error if the code is actually broken,
    // so we don't fire onErrorCallbacks here.
    this.recreateIframe()
  }

  private recreateIframe(): void {
    // Remove old iframe
    window.removeEventListener('message', this.handleMessage)
    this.container.removeChild(this.iframe)

    // Create new iframe with same settings
    this.iframe = document.createElement('iframe')
    this.iframe.style.border = 'none'
    this.iframe.style.width = '100%'
    this.iframe.style.height = '100%'
    this.iframe.sandbox.add('allow-scripts')
    this.iframe.allow = 'autoplay'
    this.iframe.src = this.iframeSrc

    this.ready = false
    this.pendingCart = null
    this.pendingStartBoot = false
    this.pendingCaptureResolve = null

    // Re-attach message listener and add to DOM
    window.addEventListener('message', this.handleMessage)
    this.container.appendChild(this.iframe)
  }

  // ============================================================
  // LIFECYCLE
  // ============================================================

  /**
   * Check if the console is ready
   */
  isReady(): boolean {
    return this.ready
  }

  /**
   * Register a callback for when the iframe is frozen and recreated
   */
  onFrozen(callback: FrozenCallback): void {
    this.onFrozenCallbacks.push(callback)
  }

  /**
   * Destroy the console host and clean up
   */
  destroy(): void {
    this.stopWatchdog()
    window.removeEventListener('message', this.handleMessage)
    this.container.removeChild(this.iframe)
    this.onReadyCallbacks = []
    this.onErrorCallbacks = []
    this.onLogCallbacks = []
    this.onCartLoadedCallbacks = []
    this.onBootCompleteCallbacks = []
    this.onFrozenCallbacks = []
  }
}
